/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable react/require-default-props */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/AppRoutes';
import ContactSupportIcon from '@/Assets/Images/contact-support.svg?url';
import LanguageIcon from '@/Assets/Images/language-icon.svg?url';
import BackgroundImage from '@/Assets/Images/stage-background.png';
import Logo from '@/Assets/Images/swiftpal-logo.svg?url';
import { useSnackbar } from '@/hooks/useSnackbar';
import { AtHomeLoginResponse, GenericApiResponse, VerifyOtpParameters } from '@/services/api';
import { isApiResponseSuccess } from '@/utils/helper';

import LocationHeader from './LocationHeader';

interface OtpVerificationProperties {
  mobile: string;
  dialCode: string;
  onBack: () => void;
  onSubmitOtp: (
    parameters: VerifyOtpParameters,
    onSuccess?: (data: GenericApiResponse<AtHomeLoginResponse>) => void,
  ) => void;
  onNavigateToProfile?: () => void;
  onResendOtp: () => void;
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

function OtpVerification({
  mobile,
  dialCode,
  onBack,
  onSubmitOtp,
  onNavigateToProfile,
  onResendOtp,
}: OtpVerificationProperties) {
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState(85);
  const [values, setValues] = useState<string[]>(Array.from({ length: 6 }).fill('') as string[]);
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['task', 'atHome', 'studentLogin']);
  const inputReferences = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((previous) => {
        if (previous <= 1) {
          return 0;
        }
        return previous - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const onTestCodeChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { nativeEvent, target } = event;
    let { value } = target;
    const inputEvent = nativeEvent as InputEvent;
    const isBackspace = inputEvent.inputType === 'deleteContentBackward';
    if (value.length > 1) {
      value = value.slice(-1);
      target.value = value;
    }

    const isNumeric = /^\d*$/.test(value);
    if (!isNumeric && !isBackspace) {
      showSnackbar(t('testCodeTypeWarning'), 'warning');
      target.value = values[index];
      return;
    }
    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    if (value.length === 1 && index < values.length - 1) {
      const nextInput = document.querySelector<HTMLInputElement>(`#input-${index + 1}`);
      nextInput?.focus();
    } else if (value === '' && index > 0) {
      const previousInput = document.querySelector<HTMLInputElement>(`#input-${index - 1}`);
      previousInput?.focus();
    }
  };

  const setInputReference = useCallback((element: HTMLInputElement | null, index: number) => {
    inputReferences.current[index] = element;
  }, []);

  const onHandleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace' && !values[index] && index > 0) {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      inputReferences.current[index - 1]?.focus();
    }
  };
  const isSubmitDisabled = useMemo(() => {
    return !values.every((value) => value.length === 1);
  }, [values]);

  const isResendDisabled = useMemo(() => {
    return timeRemaining > 0;
  }, [timeRemaining]);

  const onHandleResendClick = () => {
    if (!isResendDisabled) {
      onResendOtp();
      setTimeRemaining(85);
      setValues(Array.from({ length: 6 }).fill('') as string[]);
    }
  };

  const menuOption = [
    {
      onClick: () => {
        navigate(-1);
      },
      label: t('atHome:language'),
      icon: <img src={LanguageIcon} alt="Language" style={{ width: '24px', height: '24px' }} />,
    },
    {
      onClick: () => {
        navigate(-1);
      },
      label: t('atHome:contactSupport'),
      icon: (
        <img
          src={ContactSupportIcon}
          alt="support-icon"
          style={{ width: '24px', height: '24px' }}
        />
      ),
    },
  ];

  const onHandleVerify = () => {
    const formData = {
      mobile: `${dialCode}${mobile}`,
      otp: values.join(''),
    };
    onSubmitOtp(formData, (response) => {
      if (isApiResponseSuccess(response)) {
        if (onNavigateToProfile) {
          onNavigateToProfile();
        } else {
          navigate(`${ROUTES.AT_HOME}?step=profile`);
        }
      }
    });
  };
  return (
    <div
      className="absolute inset-0 flex h-screen w-screen flex-col bg-cover bg-no-repeat"
      style={{
        backgroundImage: `url(${BackgroundImage})`,
      }}
    >
      <LocationHeader title="OTP Verification" menuOptions={menuOption} onBack={onBack} />
      <div className="flex items-center justify-center pt-[117px]">
        <div className="flex flex-col items-center justify-center">
          <p className="text-xl1 font-semibold text-neutral-white">
            {t('atHome:enterCodeFromSms')}
          </p>
          <span className="text-xl1 font-semibold text-secondary-blue-1">
            {dialCode} {mobile}
          </span>
          <div className="mt-6 flex items-center gap-2 bg-neutral-white">
            <span className="text-xl1 font-semibold text-error-red-3">
              {formatTime(timeRemaining)}
            </span>
          </div>
          <div className="mt-4 flex flex-row items-center justify-center gap-2 md:mt-6 lg:mt-8">
            {values.map((value, index) => (
              <input
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                id={`input-${index}`}
                ref={(element) => setInputReference(element, index)}
                type="text"
                maxLength={1}
                value={value}
                onKeyDown={(event) => onHandleKeyDown(event, index)}
                onChange={(event) => onTestCodeChange(event, index)}
                className="h-[80px] w-[70px] rounded-lg border-[2.24px] border-primary-blue-3 text-center text-[2.25rem] font-semibold text-neutral-grey-4 focus:outline-none focus:ring-1 focus:ring-secondary-blue-3"
              />
            ))}
          </div>
          <div className="mt-10 flex items-center justify-center gap-1 text-2xl font-semibold">
            <p className="text-neutral-white">{t('atHome:didNotReceiveOtp')}</p>
            <button
              type="button"
              onClick={onHandleResendClick}
              className={`text-primary-yellow-3 ${
                isResendDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              disabled={isResendDisabled}
            >
              {t('atHome:resendButton')}
            </button>
          </div>
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={onHandleVerify}
              disabled={isSubmitDisabled}
              className={`font-poppins flex w-[584px] items-center justify-center rounded-full px-6 py-[25px] ${
                isSubmitDisabled
                  ? 'cursor-not-allowed bg-primary-yellow-1 text-primary-blue-1'
                  : 'bg-primary-yellow-3 text-primary-blue-3'
              } text-[1.625rem] font-semibold`}
              style={{ boxShadow: '0px 4.37px 4.37px 0px #00000040' }}
            >
              {t('studentLogin:verify')}
            </button>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 right-8">
        <img src={Logo} alt="logo" className="h-full w-full" />
      </div>
    </div>
  );
}

export default OtpVerification;
